/**
 * BaseImageInfoCard
 *
 * Displays the configured base image reference, its resolved digest,
 * and the most recent workspace build timestamp + hash.
 * Shown on the Feature Registry admin page above the feature table.
 */

import { type ReactElement } from 'react';
import { useBaseImageInfo } from '../../hooks/useBaseImageInfo';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function BaseImageInfoCard(): ReactElement {
  const { data, isLoading, isError } = useBaseImageInfo();

  if (isLoading) {
    return (
      <div className="base-image-card base-image-card--loading" aria-busy="true">
        <span className="base-image-card__loading-text">Loading base image info...</span>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="base-image-card base-image-card--error">
        <span className="base-image-card__error-text">Could not load base image info.</span>
      </div>
    );
  }

  const digestDisplay = data.digest
    ? data.digest.replace('sha256:', '').slice(0, 16) + '…'
    : '—';
  const digestTitle = data.digest ?? undefined;

  return (
    <div className="base-image-card">
      <h2 className="base-image-card__title">Base Image</h2>
      <dl className="base-image-card__grid">
        <dt>Image</dt>
        <dd className="base-image-card__mono" title={data.full_ref}>{data.full_ref}</dd>

        <dt>Version</dt>
        <dd className="base-image-card__mono">{data.version ?? '—'}</dd>

        <dt>Digest</dt>
        <dd className="base-image-card__mono" title={digestTitle}>{digestDisplay}</dd>

        <dt>Built</dt>
        <dd>{data.built_at ? formatDate(data.built_at) : '—'}</dd>
      </dl>
    </div>
  );
}
